<script lang="ts" setup>
import { useWindowScroll } from '@vueuse/core'
import { useTranslation } from 'i18next-vue'
import { Button, Drawer, PanelMenu } from 'primevue'
import type { MenuItem } from 'primevue/menuitem'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import LanguageSelector from '@/components/common/LanguageSelector.vue'
import StoreButtons from '@/components/site/StoreButtons.vue'
import PvIcon from '@/components/ui/PvIcon.vue'
import { headerNav, type NavItem } from '@/constants/navigation'

const { y } = useWindowScroll()
const { t } = useTranslation()
const route = useRoute()

const showMenu = ref(false)

watch(
  () => route.path,
  () => {
    showMenu.value = false
  },
)

const toMenuItem = (item: NavItem): MenuItem => ({
  label: t(item.labelKey),
  route: item.to,
  items: item.children?.map(toMenuItem),
})

const items = computed<MenuItem[]>(() => headerNav.map(toMenuItem))
</script>

<template>
  <header class="site-topbar">
    <div :class="{ 'shadow-xl': y > 24 }" class="site-topbar-surface">
      <RouterLink class="shrink-0 px-2" to="/">
        <img alt="Pin vision" src="../../assets/brand/logo.svg?link" width="171" height="50" />
      </RouterLink>

      <Button :aria-label="$t('common:openMenu')" class="ml-auto" text @click="showMenu = true">
        <PvIcon name="menu" class="w-6 h-6" />
      </Button>
    </div>
  </header>

  <Drawer v-model:visible="showMenu" position="full">
    <template #header>
      <img alt="Pin vision" src="../../assets/brand/logo.svg?link" width="171" height="50" />
    </template>

    <nav :aria-label="$t('common:primaryNavigation')">
      <PanelMenu :model="items" class="w-full">
        <template #item="{ item, props, hasSubmenu }">
          <RouterLink
            v-if="item.route"
            :class="{ 'bg-surface-100': item.route === route.path }"
            :to="item.route"
            class="block py-3 px-4 font-semibold text-lg rounded-lg"
          >
            {{ item.label }}
          </RouterLink>
          <button
            v-else
            class="flex items-center justify-between w-full text-left py-3 px-4 font-semibold text-lg rounded-lg"
            type="button"
            v-bind="props.action"
          >
            {{ item.label }}
            <PvIcon v-if="hasSubmenu" name="chevron-down" class="w-5 h-5" />
          </button>
        </template>
      </PanelMenu>
    </nav>

    <section class="flex gap-4 flex-col items-center py-8">
      <StoreButtons stacked />
      <LanguageSelector />
    </section>

    <template #footer>
      <Button as="router-link" class="p-button-website w-full" to="/admin">
        {{ $t('common:headerLogin') }}
      </Button>
    </template>
  </Drawer>
</template>

<style lang="scss" scoped>
@reference '../../assets/styles/root.scss';
:deep(.p-panelmenu-panel) {
  @apply border-none shadow-none;
}
</style>
